import { useState, useEffect, useCallback } from 'react';
import { adminBlogAPI } from '../../lib/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Loader2, Plus, Sparkles, Save, Trash2, Edit3, Eye, RefreshCcw, Image as ImageIcon,
  Calendar, Clock, FileText, Settings, Wand2, X, Search
} from 'lucide-react';
import { toast } from 'sonner';
import { blogImageURL } from '../../lib/blogApi';

const TONE_OPTIONS = ['professional', 'conversational', 'technical', 'casual', 'authoritative'];
const LENGTH_OPTIONS = ['short', 'medium', 'long'];
const MODEL_OPTIONS = [
  { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5 (Recommended)' },
  { value: 'gpt-5.2', label: 'GPT-5.2' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
];

export default function AdminBlogPage() {
  const [tab, setTab] = useState('posts');

  // Posts
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);  // post being edited
  const [editorOpen, setEditorOpen] = useState(false);

  // AI Settings
  const [aiSettings, setAiSettings] = useState({
    enabled: false, posts_per_day: 1, schedule_hour: 9, schedule_minute: 0,
    model: 'claude-sonnet-4-5-20250929', tone: 'professional', length: 'medium',
    niche_topics: [], auto_generate_image: true,
    image_style: 'modern abstract digital art, vibrant colors',
    auto_publish: true,
  });
  const [aiLoading, setAiLoading] = useState(true);
  const [aiSaving, setAiSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  const [manualTopic, setManualTopic] = useState('');

  const loadPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      const r = await adminBlogAPI.list();
      setPosts(r.data || []);
    } catch (e) { toast.error('Failed to load posts'); }
    finally { setPostsLoading(false); }
  }, []);

  const loadSettings = useCallback(async () => {
    setAiLoading(true);
    try {
      const r = await adminBlogAPI.getAISettings();
      setAiSettings({ ...aiSettings, ...r.data });
    } catch (e) { /* ignore */ }
    finally { setAiLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadPosts(); loadSettings(); }, [loadPosts, loadSettings]);

  const filtered = posts.filter(p =>
    !search || (p.title || '').toLowerCase().includes(search.toLowerCase())
  );

  const openNew = () => {
    setEditing({
      title: '', slug: '', meta_description: '', meta_keywords: '',
      excerpt: '', content: '', featured_image_url: '', category: 'AI Voice',
      tags: [], author: 'DotCall Team', status: 'draft',
    });
    setEditorOpen(true);
  };

  const openEdit = (p) => {
    setEditing({ ...p, tags: p.tags || [] });
    setEditorOpen(true);
  };

  const savePost = async () => {
    if (!editing.title || !editing.content) { toast.error('Title and content are required'); return; }
    try {
      const payload = { ...editing };
      if (typeof payload.tags === 'string') payload.tags = payload.tags.split(',').map(t => t.trim()).filter(Boolean);
      if (editing.id) {
        await adminBlogAPI.update(editing.id, payload);
        toast.success('Post updated');
      } else {
        await adminBlogAPI.create(payload);
        toast.success('Post created');
      }
      setEditorOpen(false);
      loadPosts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this post permanently?')) return;
    try {
      await adminBlogAPI.delete(id);
      toast.success('Deleted');
      loadPosts();
    } catch (e) { toast.error('Delete failed'); }
  };

  const regenImage = async (id) => {
    toast.loading('Generating new image…', { id: 'regen' });
    try {
      await adminBlogAPI.regenerateImage(id);
      toast.success('Image regenerated', { id: 'regen' });
      loadPosts();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Image regeneration failed';
      toast.error(msg, { id: 'regen', duration: 8000 });
    }
  };

  const saveAISettings = async () => {
    setAiSaving(true);
    try {
      await adminBlogAPI.updateAISettings(aiSettings);
      toast.success('AI settings saved — scheduler updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setAiSaving(false); }
  };

  const generateNow = async () => {
    setGenerating(true);
    toast.loading('AI is writing your post… this may take 30-90s', { id: 'gen' });
    try {
      const r = await adminBlogAPI.generateNow(manualTopic || null);
      toast.success(`Created: ${r.data.title}`, { id: 'gen' });
      setManualTopic('');
      loadPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed', { id: 'gen' });
    }
    finally { setGenerating(false); }
  };

  const addTopic = () => {
    if (!newTopic.trim()) return;
    setAiSettings({ ...aiSettings, niche_topics: [...aiSettings.niche_topics, newTopic.trim()] });
    setNewTopic('');
  };

  const removeTopic = (idx) => {
    const next = [...aiSettings.niche_topics];
    next.splice(idx, 1);
    setAiSettings({ ...aiSettings, niche_topics: next });
  };

  return (
    <div className="space-y-6" data-testid="admin-blog-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit'] flex items-center gap-3">
            <FileText className="w-6 h-6 text-amber-400" /> Blog Management
          </h1>
          <p className="text-sm text-slate-400 mt-1">Create posts manually or let AI handle it daily.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#0B1221] border border-white/5">
          <TabsTrigger value="posts" data-testid="tab-posts">Posts ({posts.length})</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai-settings"><Sparkles className="w-3 h-3 mr-1" />AI Auto-Blogger</TabsTrigger>
        </TabsList>

        {/* POSTS */}
        <TabsContent value="posts" className="space-y-4 mt-6">
          <div className="flex gap-3 items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search posts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-[#0B1221] border-white/10 text-white"
                data-testid="post-search"
              />
            </div>
            <Button onClick={openNew} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="new-post-btn">
              <Plus className="w-4 h-4 mr-2" /> New Post
            </Button>
          </div>

          {postsLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-amber-500 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500 border border-dashed border-white/10 rounded-xl" data-testid="no-posts">
              No posts yet. Create one or enable AI auto-blogger.
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(p => (
                <div key={p.id} className="bg-[#0B1221] border border-white/5 rounded-xl p-4 flex gap-4 items-start hover:border-amber-500/30 transition-all" data-testid={`post-row-${p.slug}`}>
                  <div className="w-20 h-20 rounded-lg bg-white/5 overflow-hidden flex-shrink-0 flex items-center justify-center">
                    {p.featured_image_url
                      ? <img src={blogImageURL(p.featured_image_url)} alt="" className="w-full h-full object-cover" />
                      : <ImageIcon className="w-6 h-6 text-slate-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className={p.status === 'published' ? 'border-green-500/40 text-green-400' : 'border-slate-500/40 text-slate-400'}>
                        {p.status}
                      </Badge>
                      {p.ai_generated && <Badge variant="outline" className="border-purple-500/40 text-purple-400">✨ AI</Badge>}
                      <span className="text-xs text-slate-500">{p.category}</span>
                    </div>
                    <h3 className="font-semibold text-white truncate">{p.title}</h3>
                    <p className="text-xs text-slate-500 line-clamp-1 mt-1">{p.excerpt}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-2">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{(p.published_at || p.created_at || '').slice(0, 10)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{p.reading_time || 5} min</span>
                      <span>👁 {p.view_count || 0}</span>
                      <span className="text-slate-600">/{p.slug}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => window.open(`/blog/${p.slug}`, '_blank')} className="text-slate-400 hover:text-white" data-testid={`view-${p.slug}`}>
                      <Eye className="w-3 h-3 mr-1" /> View
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="text-slate-400 hover:text-amber-400" data-testid={`edit-${p.slug}`}>
                      <Edit3 className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => regenImage(p.id)} className="text-slate-400 hover:text-purple-400" data-testid={`regen-img-${p.slug}`}>
                      <RefreshCcw className="w-3 h-3 mr-1" /> Image
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deletePost(p.id)} className="text-slate-400 hover:text-red-400" data-testid={`delete-${p.slug}`}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* AI SETTINGS */}
        <TabsContent value="ai" className="space-y-6 mt-6">
          {aiLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-amber-500 animate-spin" /></div>
          ) : (
            <>
              <div className="bg-gradient-to-br from-amber-500/10 to-purple-500/5 border border-amber-500/20 rounded-2xl p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Wand2 className="w-5 h-5 text-amber-400" />
                      Auto-Blogger Status
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">When enabled, AI writes & publishes posts daily using your topic pool.</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={aiSettings.enabled}
                      onCheckedChange={(v) => setAiSettings({ ...aiSettings, enabled: v })}
                      data-testid="ai-enabled-switch"
                    />
                    <span className={`text-sm font-medium ${aiSettings.enabled ? 'text-green-400' : 'text-slate-500'}`}>
                      {aiSettings.enabled ? 'Active' : 'Paused'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-[#0B1221] border border-white/5 rounded-xl p-5 space-y-3">
                  <Label className="text-slate-300">Posts per day</Label>
                  <Input type="number" min="1" max="10" value={aiSettings.posts_per_day}
                    onChange={(e) => setAiSettings({ ...aiSettings, posts_per_day: parseInt(e.target.value) || 1 })}
                    className="bg-[#020617] border-white/10 text-white" data-testid="posts-per-day" />
                  <p className="text-xs text-slate-500">1–10 posts per day. Counts on selected hour daily.</p>
                </div>

                <div className="bg-[#0B1221] border border-white/5 rounded-xl p-5 space-y-3">
                  <Label className="text-slate-300">Daily run time (UTC)</Label>
                  <div className="flex gap-2">
                    <Input type="number" min="0" max="23" value={aiSettings.schedule_hour}
                      onChange={(e) => setAiSettings({ ...aiSettings, schedule_hour: parseInt(e.target.value) || 0 })}
                      className="bg-[#020617] border-white/10 text-white" data-testid="schedule-hour" />
                    <span className="text-slate-400 self-center">:</span>
                    <Input type="number" min="0" max="59" value={aiSettings.schedule_minute}
                      onChange={(e) => setAiSettings({ ...aiSettings, schedule_minute: parseInt(e.target.value) || 0 })}
                      className="bg-[#020617] border-white/10 text-white" data-testid="schedule-minute" />
                  </div>
                  <p className="text-xs text-slate-500">Hour and minute in UTC. Server checks hourly.</p>
                </div>

                <div className="bg-[#0B1221] border border-white/5 rounded-xl p-5 space-y-3">
                  <Label className="text-slate-300">Model</Label>
                  <Select value={aiSettings.model} onValueChange={(v) => setAiSettings({ ...aiSettings, model: v })}>
                    <SelectTrigger className="bg-[#020617] border-white/10 text-white" data-testid="model-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MODEL_OPTIONS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-[#0B1221] border border-white/5 rounded-xl p-5 space-y-3">
                  <Label className="text-slate-300">Tone & Length</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={aiSettings.tone} onValueChange={(v) => setAiSettings({ ...aiSettings, tone: v })}>
                      <SelectTrigger className="bg-[#020617] border-white/10 text-white" data-testid="tone-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{TONE_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={aiSettings.length} onValueChange={(v) => setAiSettings({ ...aiSettings, length: v })}>
                      <SelectTrigger className="bg-[#020617] border-white/10 text-white" data-testid="length-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{LENGTH_OPTIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="bg-[#0B1221] border border-white/5 rounded-xl p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">Niche topic pool ({aiSettings.niche_topics.length})</Label>
                  <span className="text-xs text-slate-500">AI rotates through these topics</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newTopic}
                    onChange={(e) => setNewTopic(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addTopic()}
                    placeholder="e.g. How to scale outbound calling for SaaS in India"
                    className="bg-[#020617] border-white/10 text-white"
                    data-testid="new-topic-input"
                  />
                  <Button onClick={addTopic} variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10" data-testid="add-topic-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-1.5 max-h-60 overflow-y-auto">
                  {aiSettings.niche_topics.map((t, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg group">
                      <span className="text-sm text-slate-300 flex-1">{t}</span>
                      <button onClick={() => removeTopic(i)} className="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-topic-${i}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-[#0B1221] border border-white/5 rounded-xl p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">Auto-generate featured image</Label>
                    <Switch
                      checked={aiSettings.auto_generate_image}
                      onCheckedChange={(v) => setAiSettings({ ...aiSettings, auto_generate_image: v })}
                      data-testid="auto-image-switch"
                    />
                  </div>
                  <Input
                    value={aiSettings.image_style}
                    onChange={(e) => setAiSettings({ ...aiSettings, image_style: e.target.value })}
                    disabled={!aiSettings.auto_generate_image}
                    placeholder="Image style hint"
                    className="bg-[#020617] border-white/10 text-white"
                    data-testid="image-style-input"
                  />
                  <p className="text-xs text-slate-500">Powered by Gemini Nano Banana</p>
                </div>

                <div className="bg-[#0B1221] border border-white/5 rounded-xl p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">Auto-publish</Label>
                    <Switch
                      checked={aiSettings.auto_publish}
                      onCheckedChange={(v) => setAiSettings({ ...aiSettings, auto_publish: v })}
                      data-testid="auto-publish-switch"
                    />
                  </div>
                  <p className="text-xs text-slate-500">When off, AI posts go to drafts for your review.</p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={saveAISettings} disabled={aiSaving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-ai-settings">
                  {aiSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Settings
                </Button>
              </div>

              <div className="bg-gradient-to-br from-purple-500/10 to-amber-500/5 border border-purple-500/20 rounded-2xl p-6">
                <h4 className="font-semibold text-white flex items-center gap-2"><Wand2 className="w-4 h-4 text-purple-400" /> Generate a post right now</h4>
                <p className="text-sm text-slate-400 mt-1">Test the AI pipeline. Leave topic blank to use the first niche topic.</p>
                <div className="flex gap-2 mt-4">
                  <Input
                    value={manualTopic}
                    onChange={(e) => setManualTopic(e.target.value)}
                    placeholder="Optional: custom topic..."
                    className="bg-[#020617] border-white/10 text-white"
                    data-testid="manual-topic-input"
                  />
                  <Button onClick={generateNow} disabled={generating} className="bg-purple-500 hover:bg-purple-600 text-white whitespace-nowrap" data-testid="generate-now-btn">
                    {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Generate Now
                  </Button>
                </div>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* EDITOR DIALOG */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="bg-[#0B1221] border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editing?.id ? 'Edit Post' : 'Create New Post'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="bg-[#020617] border-white/10 text-white" data-testid="editor-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Slug (auto if blank)</Label>
                  <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                    className="bg-[#020617] border-white/10 text-white" data-testid="editor-slug" />
                </div>
                <div>
                  <Label>Category</Label>
                  <Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    className="bg-[#020617] border-white/10 text-white" />
                </div>
              </div>
              <div>
                <Label>Meta description</Label>
                <Textarea value={editing.meta_description} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })}
                  rows={2} className="bg-[#020617] border-white/10 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Meta keywords</Label>
                  <Input value={editing.meta_keywords} onChange={(e) => setEditing({ ...editing, meta_keywords: e.target.value })}
                    className="bg-[#020617] border-white/10 text-white" />
                </div>
                <div>
                  <Label>Tags (comma-separated)</Label>
                  <Input value={Array.isArray(editing.tags) ? editing.tags.join(', ') : editing.tags}
                    onChange={(e) => setEditing({ ...editing, tags: e.target.value })}
                    className="bg-[#020617] border-white/10 text-white" />
                </div>
              </div>
              <div>
                <Label>Excerpt</Label>
                <Textarea value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                  rows={2} className="bg-[#020617] border-white/10 text-white" />
              </div>
              <div>
                <Label>Featured image URL</Label>
                <Input value={editing.featured_image_url} onChange={(e) => setEditing({ ...editing, featured_image_url: e.target.value })}
                  placeholder="/api/blog/images/... or https://..."
                  className="bg-[#020617] border-white/10 text-white" />
              </div>
              <div>
                <Label>Content (Markdown) *</Label>
                <Textarea value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                  rows={14} className="bg-[#020617] border-white/10 text-white font-mono text-sm" data-testid="editor-content" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Author</Label>
                  <Input value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })}
                    className="bg-[#020617] border-white/10 text-white" />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger className="bg-[#020617] border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={savePost} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-post-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
